// YERİ: src/app/domains/website/core/language-preference.ts
//
// "Bu ziyaretçiye hangi dili göstermeliyiz?" — YALNIZCA dil öneki OLMAYAN bir
// adrese gelindiğinde sorulur (melbarrel.com, /pricing gibi eski bağlantılar).
//
// Sıra, en güçlü sinyalden en zayıfına:
//   1. ZİYARETÇİNİN AÇIK SEÇİMİ (çerez) — üstteki dil seçicisinden seçilmiş.
//   2. Tarayıcı dili (Accept-Language / navigator.languages).
//   3. Varsayılan dil.
//
// Açık seçim tarayıcı dilini EZER ve bu bilerek: Kanada'da yaşayan, tarayıcısı
// İngilizce ama Farsça okumayı tercih eden ziyaretçi bir kez seçer, bir daha
// sorulmaz.
//
// '/en/pricing' gibi ÖNEKLİ adreslerde bunların hiçbiri çalışmaz — URL açık bir
// niyettir. Aksi hâlde paylaşılan bir bağlantı alıcının çerezine göre başka
// dilde açılırdı ve kimse aynı sayfayı görmezdi.

import { inject, PLATFORM_ID, REQUEST } from '@angular/core';
import { isPlatformServer } from '@angular/common';
import { WebLanguage } from './web.models';

/** Ziyaretçinin açık dil seçimi. Bir yıl yaşar. */
export const LANG_COOKIE = 'melbarrel_lang';
const COOKIE_MAX_AGE = 60 * 60 * 24 * 365;

/**
 * Dil sinyallerini okuyan kaynak.
 *
 * SUNUCUDA istek başlıklarından, TARAYICIDA document/navigator'dan gelir.
 * İkisinin de AYNI sonucu vermesi şart: sunucu 'fa' seçip istemci 'en' seçseydi
 * yönlendirme bir gidip bir geri gelirdi.
 */
export interface LanguageSignals {
  /** Çerezdeki açık seçim, yoksa null. */
  chosen: string | null;
  /** Tarayıcının tercih sırası ('fa', 'en-ca' …), en güçlüden zayıfa. */
  accepted: string[];
}

/** Bileşen/guard bağlamında çağrılır (inject gerektirir). */
export function readLanguageSignals(): LanguageSignals {
  const isServer = isPlatformServer(inject(PLATFORM_ID));

  if (!isServer) {
    return {
      chosen: readCookieFromDocument(LANG_COOKIE),
      accepted: readNavigatorLanguages(),
    };
  }

  // SSR: istek başlıkları. REQUEST, @angular/ssr tarafından sağlanır; yoksa
  // (birim testi, prerender) sinyalsiz kalırız ve varsayılana düşeriz.
  const request = inject(REQUEST, { optional: true });
  if (!request) return { chosen: null, accepted: [] };

  return {
    chosen: readCookieFromHeader(request.headers.get('cookie'), LANG_COOKIE),
    accepted: parseAcceptLanguage(request.headers.get('accept-language')),
  };
}

/**
 * Sinyalleri YAYINDA olan dillerle eşleştirir.
 *
 * Eşleşme iki aşamalı: önce tam kod ('en-ca'), sonra ana dil ('en'). Böylece
 * 'fa-IR' gönderen bir tarayıcı 'fa' sayfasını bulur, ama yayında 'en-ca' varsa
 * 'en-CA' gönderen ziyaretçi genel İngilizce yerine onu alır.
 */
export function resolvePreferredLanguage(
  signals: LanguageSignals,
  languages: WebLanguage[],
  fallback: string,
): string {
  const published = new Map(languages.map((l) => [l.code.toLowerCase(), l.code]));

  const match = (tag: string): string | null => {
    const code = tag.toLowerCase();
    return published.get(code) ?? published.get(code.split('-')[0]) ?? null;
  };

  // 1. Açık seçim — tarayıcı dilini ezer.
  if (signals.chosen) {
    const chosen = match(signals.chosen);
    if (chosen) return chosen;
  }

  // 2. Tarayıcı dili.
  for (const tag of signals.accepted) {
    const found = match(tag);
    if (found) return found;
  }

  // 3. Varsayılan.
  return fallback;
}

/** Ziyaretçinin seçimini kalıcı yazar. Yalnız tarayıcıda anlamlı. */
export function rememberLanguage(code: string): void {
  if (typeof document === 'undefined') return;

  // SameSite=Lax: dış bağlantıdan gelen normal gezinmede çerez GÖNDERİLİR
  // (Strict olsaydı Google'dan gelen ziyaretçi seçimini kaybederdi).
  document.cookie =
    `${LANG_COOKIE}=${encodeURIComponent(code)}; Max-Age=${COOKIE_MAX_AGE}; Path=/; SameSite=Lax`;
}

/**
 * 'fr-CH;q=0.9, en;q=0.8, *;q=0.5' → ['fr-ch', 'en']
 *
 * q değerine göre sıralanır — tarayıcılar genelde zaten sıralı gönderir ama
 * garanti değil. '*' atılır: "her şey olur" bir tercih değildir.
 */
export function parseAcceptLanguage(header: string | null | undefined): string[] {
  if (!header) return [];

  return header
    .split(',')
    .map((part) => {
      const [tag = '', ...params] = part.trim().split(';');
      const q = params
        .map((p) => p.trim())
        .find((p) => p.startsWith('q='));

      const weight = q ? Number.parseFloat(q.slice(2)) : 1;
      return { tag: tag.trim().toLowerCase(), weight: Number.isFinite(weight) ? weight : 0 };
    })
    .filter((entry) => entry.tag && entry.tag !== '*' && entry.weight > 0)
    .sort((a, b) => b.weight - a.weight)
    .map((entry) => entry.tag);
}

function readNavigatorLanguages(): string[] {
  if (typeof navigator === 'undefined') return [];

  const list = navigator.languages?.length ? [...navigator.languages] : [navigator.language];
  return list.filter(Boolean).map((l) => l.toLowerCase());
}

function readCookieFromDocument(name: string): string | null {
  if (typeof document === 'undefined') return null;
  return readCookieFromHeader(document.cookie, name);
}

function readCookieFromHeader(header: string | null | undefined, name: string): string | null {
  if (!header) return null;

  for (const part of header.split(';')) {
    const [key, ...rest] = part.trim().split('=');
    if (key === name) {
      const value = decodeURIComponent(rest.join('=')).trim();
      return value || null;
    }
  }

  return null;
}
